import { zodResolver } from "@hookform/resolvers/zod";
import { router, usePage } from "@inertiajs/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { orderMitraReason } from "@/components/admin-portal/appointment/mitra-assignment-reason";
import { owesMitraReason } from "@/lib/appointments/form";
import {
	BULK_RESCHEDULE_FORM_SCHEMA,
	type BulkRescheduleFormSchema,
	buildPayload,
	defineFormDefaultValues,
	getChangedVisits,
} from "@/lib/appointments/form-bulk-reschedule";
import { getCsrfToken } from "@/lib/csrf";
import type { BulkRescheduleGlobalPageProps } from "@/pages/AdminPortal/Appointment/BulkReschedule";
import type { Appointment } from "@/types/admin-portal/appointment";

export const useBulkRescheduleForm = () => {
	const { url: pageURL, props: globalProps } =
		usePage<BulkRescheduleGlobalPageProps>();

	const [isLoading, setIsLoading] = useState(false);
	const [isConfirmOpen, setIsConfirmOpen] = useState(false);

	const appointments = useMemo(
		() => globalProps.order?.appointments || [],
		[globalProps.order?.appointments],
	) as Appointment[];

	const formDefaultValues = useMemo(
		() => defineFormDefaultValues(appointments),
		[appointments],
	);

	// Track original values for change detection
	const originalValues = useMemo(() => {
		return formDefaultValues.rescheduleRequests.map((req) => ({
			...req,
			appointmentDateTime: req.appointmentDateTime,
		}));
	}, [formDefaultValues.rescheduleRequests]);

	const form = useForm<BulkRescheduleFormSchema>({
		resolver: zodResolver(BULK_RESCHEDULE_FORM_SCHEMA),
		defaultValues: { ...formDefaultValues },
		mode: "onChange",
	});

	const currentValues = form.watch("rescheduleRequests");
	const watchReason = form.watch("reason") || "";
	const watchSendEmail = form.watch("sendEmailNotification") ?? true;
	const watchBypassSlotConflict = form.watch("bypassSlotConflict") ?? false;
	const watchBypassContractCheck = form.watch("bypassContractCheck") ?? false;
	const watchBypassJourneyCheck = form.watch("bypassJourneyCheck") ?? false;

	// Detect changed visits
	const changedVisits = useMemo(() => {
		return getChangedVisits(currentValues, originalValues);
	}, [currentValues, originalValues]);

	const hasChanges = changedVisits.length > 0;

	// One reason per order: once it has one, the server copies it onto every visit
	// newly put on a Mitra, so the admin is asked only when the order has none.
	const [mitraAssignmentReason, setMitraAssignmentReason] = useState("");
	const mitraReasonVisitIds =
		globalProps.mitraReason && !orderMitraReason(globalProps.mitraReason)
			? changedVisits
					.filter((visit) =>
						owesMitraReason(
							originalValues.find(
								(o) => o.appointmentId === visit.appointmentId,
							)?.therapist,
							visit.therapist,
						),
					)
					.map((visit) => visit.appointmentId)
			: [];

	// Get summary for confirmation modal (all visits with their current values)
	const getSummary = useCallback(() => {
		return currentValues.map((visit) => {
			const original = originalValues.find(
				(o) => o.appointmentId === visit.appointmentId,
			);
			const appointment = appointments.find(
				(a) => a.id === visit.appointmentId,
			);

			return {
				visitNumber: appointment?.visitNumber || 0,
				oldDateTime: original?.appointmentDateTime,
				newDateTime: visit.appointmentDateTime,
				oldTherapistId: original?.therapist?.id,
				newTherapistId: visit.therapist?.id,
				newTherapistName: visit.therapist?.name,
				appointment,
			};
		});
	}, [currentValues, originalValues, appointments]);

	// Handle confirmation modal open
	const openConfirmModal = useCallback(() => {
		if (!hasChanges) {
			form.setError("root", {
				type: "custom",
				message: "No changes detected. Please modify at least one visit.",
			});
			return;
		}
		setIsConfirmOpen(true);
	}, [hasChanges, form]);

	// Handle confirmation modal close
	const closeConfirmModal = useCallback(() => {
		setIsConfirmOpen(false);
	}, []);

	// `disabled={isLoading}` engages only after a re-render, so a second click in
	// the same tick still posts a second bulk reschedule. Same guard as New.tsx.
	const isSubmittingRef = useRef(false);

	// Handle actual submission
	const onConfirmSubmit = useCallback(() => {
		if (isSubmittingRef.current) return; // a submit is already in flight
		setIsConfirmOpen(false);

		const orderId = globalProps.order?.id;
		if (!orderId) {
			console.error("Order ID is missing, cannot build payload");
			return;
		}

		setIsLoading(true);

		const reason = form.getValues("reason") || "";
		const sendEmail = form.getValues("sendEmailNotification") ?? true;
		const payload = buildPayload(changedVisits, sendEmail, reason, {
			bypassSlotConflict: form.getValues("bypassSlotConflict") ?? false,
			bypassContractCheck: form.getValues("bypassContractCheck") ?? false,
			bypassJourneyCheck: form.getValues("bypassJourneyCheck") ?? false,
		});
		for (const item of payload.items) {
			if (mitraReasonVisitIds.includes(item.appointment_id))
				item.mitra_assignment_reason = mitraAssignmentReason;
		}

		const csrfToken = getCsrfToken();

		isSubmittingRef.current = true;
		fetch(`/api/v1/bookings/${orderId}/appointments/reschedule/bulk`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				"X-CSRF-Token": csrfToken,
			},
			body: JSON.stringify(payload),
		})
			.then(async (res) => {
				const data = await res.json();
				if (!res.ok) {
					throw data;
				}
				return data;
			})
			.then((data) => {
				const count = data?.data?.rescheduled_count || 0;
				toast.success(
					`${count} appointment${count !== 1 ? "s" : ""} successfully rescheduled.`,
				);

				const ordersURL =
					globalProps.adminPortal?.router?.adminPortal?.appointment?.orders;
				if (ordersURL) {
					router.visit(
						`${ordersURL}?search=${globalProps.order?.registrationNumber}`,
						{
							preserveScroll: true,
							preserveState: false,
						},
					);
				}
			})
			.catch((err) => {
				console.error("Bulk reschedule failed:", err);

				let errorMsg = "Failed to reschedule appointments";
				if (typeof err?.error === "string") {
					errorMsg = err.error;
				} else if (Array.isArray(err?.error)) {
					errorMsg = err.error
						.filter((e: any) => e?.message)
						.map((e: any) => e.message)
						.join(", ");
				} else if (err?.error?.message) {
					errorMsg = err.error.message;
				}

				form.setError("root", {
					type: "custom",
					message: errorMsg,
				});
			})
			.finally(() => {
				isSubmittingRef.current = false;
				setTimeout(() => setIsLoading(false), 250);
				console.groupEnd();
			});
	}, [
		globalProps,
		form,
		changedVisits,
		mitraReasonVisitIds,
		mitraAssignmentReason,
	]);

	// Handle bulk actions
	const applyTherapistToAll = useCallback(
		(
			therapist: {
				id: string;
				name: string;
				registrationNumber?: string;
				employmentType?: string;
				gender?: string;
				activeAddress?: { location?: { city: string } };
			} | null,
		) => {
			const currentRequests = form.getValues("rescheduleRequests");
			// Applying a therapist also aligns the bulk preferred gender to that
			// therapist's gender (kept in sync across every request).
			const nextGender = therapist?.gender;
			const updatedRequests = currentRequests.map((req) => {
				const preferredTherapistGender =
					nextGender ?? req.preferredTherapistGender;
				// Only rows with a date can hold a therapist, but every row adopts the
				// gender so the bulk control (derived from row 0) never goes stale.
				if (!req.appointmentDateTime) {
					return { ...req, preferredTherapistGender };
				}

				return {
					...req,
					preferredTherapistGender,
					therapist: therapist
						? {
								id: therapist.id,
								name: therapist.name,
								registrationNumber: therapist.registrationNumber,
								employmentType: therapist.employmentType,
								gender: therapist.gender,
								locationCity: therapist.activeAddress?.location?.city,
							}
						: null,
				};
			});
			form.setValue("rescheduleRequests", updatedRequests, {
				shouldDirty: true,
				shouldValidate: true,
			});
		},
		[form],
	);

	// Reset all appointments to original values
	const resetAll = useCallback(() => {
		form.setValue("rescheduleRequests", originalValues, {
			shouldDirty: false,
			shouldValidate: true,
		});
	}, [form, originalValues]);

	// Side-effect for server validation
	useEffect(() => {
		if (!globalProps?.errors) return;

		for (const [key, value] of Object.entries(globalProps?.errors)) {
			form.setError(key as any, {
				type: "custom",
				message: (value as string[]).join(", "),
			});
		}
	}, [globalProps.errors, form.setError]);

	const setReason = useCallback(
		(value: string) => {
			form.setValue("reason", value, { shouldDirty: true });
		},
		[form],
	);

	const setSendEmail = useCallback(
		(value: boolean) => {
			form.setValue("sendEmailNotification", value, { shouldDirty: true });
		},
		[form],
	);

	const setBypassSlotConflict = useCallback(
		(value: boolean) => {
			form.setValue("bypassSlotConflict", value, { shouldDirty: true });
		},
		[form],
	);

	const setBypassContractCheck = useCallback(
		(value: boolean) => {
			form.setValue("bypassContractCheck", value, { shouldDirty: true });
		},
		[form],
	);

	const setBypassJourneyCheck = useCallback(
		(value: boolean) => {
			form.setValue("bypassJourneyCheck", value, { shouldDirty: true });
		},
		[form],
	);

	return {
		form,
		isLoading,
		hasChanges,
		changedVisits,
		isConfirmOpen,
		openConfirmModal,
		closeConfirmModal,
		onConfirmSubmit,
		applyTherapistToAll,
		resetAll,
		getSummary,
		appointments,
		pageURL,
		watchReason,
		setReason,
		watchSendEmail,
		setSendEmail,
		watchBypassSlotConflict,
		setBypassSlotConflict,
		watchBypassContractCheck,
		setBypassContractCheck,
		watchBypassJourneyCheck,
		setBypassJourneyCheck,
		mitraReason: mitraReasonVisitIds.length
			? {
					options: globalProps.mitraReason?.options ?? [],
					value: mitraAssignmentReason,
					onChange: setMitraAssignmentReason,
					required: true,
				}
			: undefined,
	};
};
